import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@900&family=DM+Sans:wght@300;400;500&display=swap');

  @keyframes fadeSlide {
    from { opacity: 0; transform: scale(1.03); }
    to   { opacity: 1; transform: scale(1); }
  }
  @keyframes shimmer {
    0%   { background-position: -200% center; }
    100% { background-position:  200% center; }
  }
  @keyframes progressBar {
    from { width: 0%; }
    to   { width: 100%; }
  }

  .banner-root {
    position: relative;
    width: 100%;
    /* 16:5 ratio — matches 1440×500 */
    aspect-ratio: 1440 / 500;
    max-height: 560px;
    min-height: 220px;
    overflow: hidden;
    background: #0e0202;
    cursor: pointer;
    display: block;
  }

  .banner-slide {
    position: absolute;
    inset: 0;
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.65s ease;
  }
  .banner-slide.active {
    opacity: 1;
    pointer-events: auto;
    animation: fadeSlide 0.65s ease forwards;
  }

  .banner-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
    display: block;
    user-select: none;
    -webkit-user-drag: none;
  }

  /* Gradient overlay so nav controls are always legible */
  .banner-overlay {
    position: absolute;
    inset: 0;
    background:
      linear-gradient(to right, rgba(14,2,2,0.55) 0%, transparent 35%, transparent 65%, rgba(14,2,2,0.55) 100%),
      linear-gradient(to top,   rgba(14,2,2,0.70) 0%, transparent 50%);
    pointer-events: none;
  }

  /* ── Prev / Next arrows ── */
  .banner-arrow {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    z-index: 10;
    width: 44px;
    height: 44px;
    border-radius: 50%;
    border: 1px solid rgba(255,255,255,0.22);
    background: rgba(0,0,0,0.35);
    color: #fff;
    font-size: 18px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: background 0.2s, border-color 0.2s, transform 0.2s;
    backdrop-filter: blur(6px);
    -webkit-backdrop-filter: blur(6px);
  }
  .banner-arrow:hover {
    background: rgba(192,57,43,0.75);
    border-color: rgba(192,57,43,0.8);
    transform: translateY(-50%) scale(1.08);
  }
  .banner-arrow.prev { left: 18px; }
  .banner-arrow.next { right: 18px; }

  /* ── Dot indicators ── */
  .banner-dots {
    position: absolute;
    bottom: 18px;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    gap: 8px;
    align-items: center;
    z-index: 10;
  }
  .banner-dot {
    height: 6px;
    border-radius: 3px;
    background: rgba(255,255,255,0.28);
    cursor: pointer;
    transition: background 0.3s, width 0.35s cubic-bezier(0.34,1.56,0.64,1);
    width: 6px;
  }
  .banner-dot.active {
    background: #E8523A;
    width: 22px;
    box-shadow: 0 0 8px rgba(232,82,58,0.65);
  }

  /* ── Thin progress bar at bottom ── */
  .banner-progress {
    position: absolute;
    bottom: 0;
    left: 0;
    height: 3px;
    background: linear-gradient(90deg, #C0392B, #E8523A);
    z-index: 11;
    animation: progressBar 7s linear forwards;
  }

  /* ── Slide label (optional caption overlay) ── */
  .banner-label {
    position: absolute;
    bottom: 40px;
    left: 0;
    right: 0;
    text-align: center;
    z-index: 9;
    pointer-events: none;
    padding: 0 80px;
  }
  .banner-label-title {
    font-family: 'Playfair Display', serif;
    font-size: clamp(18px, 3vw, 34px);
    font-weight: 900;
    color: #fff;
    letter-spacing: -0.02em;
    line-height: 1.2;
    margin: 0 0 4px;
    text-shadow: 0 2px 16px rgba(0,0,0,0.6);
  }
  .banner-label-sub {
    font-family: 'DM Sans', sans-serif;
    font-size: clamp(11px, 1.4vw, 14px);
    font-weight: 300;
    color: rgba(255,255,255,0.55);
    letter-spacing: 0.06em;
    margin: 0;
  }

  /* ── "Shop Now" pill ── */
  .banner-cta {
    display: inline-block;
    margin-top: 10px;
    padding: 8px 22px;
    background: linear-gradient(135deg, #C0392B, #8B1C0E);
    color: #fff;
    font-family: 'DM Sans', sans-serif;
    font-size: 12px;
    font-weight: 500;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    border-radius: 50px;
    box-shadow: 0 4px 18px rgba(192,57,43,0.5);
    pointer-events: auto;
    border: none;
    cursor: pointer;
    transition: transform 0.2s, box-shadow 0.2s;
  }
  .banner-cta:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(192,57,43,0.65);
  }

  @media (max-width: 640px) {
    .banner-arrow { width: 34px; height: 34px; font-size: 14px; }
    .banner-arrow.prev { left: 10px; }
    .banner-arrow.next { right: 10px; }
    .banner-label { padding: 0 52px; bottom: 34px; }
    .banner-dot { height: 5px; }
    .banner-dot.active { width: 16px; }
  }
`;

/**
 * Hero — Full-width banner carousel.
 *
 * Props:
 *  banners  – Array of banner objects:
 *    {
 *      id:       string | number      (unique key)
 *      image:    string               (URL — 1440×500 px recommended)
 *      title:    string               (optional caption)
 *      subtitle: string               (optional sub-caption)
 *      href:     string               (optional per-banner link; falls back to "/store")
 *    }
 *
 * All banners redirect to /store (or banner.href) on click.
 * If no banners are passed, the component returns null.
 */
export default function Hero({ banners = [] }) {
  const navigate = useNavigate();
  const [current, setCurrent] = useState(0);
  const [progressKey, setProgressKey] = useState(0);
  const total = banners.length;

  const go = useCallback((idx) => {
    setCurrent((idx + total) % total);
    setProgressKey((k) => k + 1);
  }, [total]);

  // Auto-advance every 4 s
  useEffect(() => {
    if (total <= 1) return;
    const t = setInterval(() => go(current + 1), 4000);
    return () => clearInterval(t);
  }, [current, total, go]);

  if (total === 0) return null;

  const handleBannerClick = (banner) => {
    navigate(banner.href || "/store");
  };

  return (
    <>
      <style>{css}</style>

      <div className="banner-root" role="region" aria-label="Promotional banners">

        {/* Slides */}
        {banners.map((banner, i) => (
          <div
            key={banner.id ?? i}
            className={`banner-slide ${i === current ? "active" : ""}`}
            onClick={() => handleBannerClick(banner)}
            aria-hidden={i !== current}
          >
            <img
              className="banner-img"
              src={banner.image}
              alt={banner.title || `Banner ${i + 1}`}
              draggable={false}
            />


          </div>
        ))}

        {/* Prev arrow */}
        {total > 1 && (
          <button
            className="banner-arrow prev"
            aria-label="Previous banner"
            onClick={(e) => { e.stopPropagation(); go(current - 1); }}
          >
            ‹
          </button>
        )}

        {/* Next arrow */}
        {total > 1 && (
          <button
            className="banner-arrow next"
            aria-label="Next banner"
            onClick={(e) => { e.stopPropagation(); go(current + 1); }}
          >
            ›
          </button>
        )}

        {/* Dot indicators */}
        {total > 1 && (
          <div className="banner-dots" role="tablist" aria-label="Banner navigation">
            {banners.map((_, i) => (
              <div
                key={i}
                role="tab"
                aria-selected={i === current}
                aria-label={`Banner ${i + 1}`}
                className={`banner-dot ${i === current ? "active" : ""}`}
                onClick={(e) => { e.stopPropagation(); go(i); }}
              />
            ))}
          </div>
        )}


      </div>
    </>
  );
}